import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Paper, Stepper, Step, StepLabel, Alert, CircularProgress } from '@mui/material';
import authService from '../../services/authService';

const ForgotPasswordForm = ({ onBackToLogin }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    verificationCode: '',
    newPassword: '',
    confirmPassword: ''
  });
  const steps = ['Enter Email', 'Verify Code', 'Reset Password'];

  const handleSendCode = async () => {
    setLoading(true);
    setError('');
    try {
      await authService.forgotPassword(formData.email);
      setCurrentStep(1);
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    }
    setLoading(false);
  };

  const handleVerifyCode = () => {
    setCurrentStep(2);
  };

  const handleResetPassword = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match!');
      return;
    }
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long!');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      await authService.confirmForgotPassword(formData.email, formData.verificationCode, formData.newPassword);
      onBackToLogin();
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper elevation={8} sx={{ p: 4, width: '100%', maxWidth: 400, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>Reset Password</Typography>
          <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {currentStep === 0 && (
          <>
            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
              Enter your email to receive a reset code
            </Typography>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              sx={{ mb: 3 }}
            />
            <Button
              onClick={handleSendCode}
              variant="contained"
              fullWidth
              size="large"
              disabled={!formData.email || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Send Reset Code'}
            </Button>
          </>
        )}

        {currentStep === 1 && (
          <>
            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
              We've sent a reset code to:<br/>
              <strong>{formData.email}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Reset Code"
              placeholder="Enter 6-digit code"
              value={formData.verificationCode}
              onChange={(e) => handleInputChange('verificationCode', e.target.value)}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 6 }}
            />
            <Button
              onClick={handleVerifyCode}
              variant="contained"
              fullWidth
              size="large"
              disabled={!formData.verificationCode}
            >
              Verify Code
            </Button>
          </>
        )}

        {currentStep === 2 && (
          <>
            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
              Enter your new password
            </Typography>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={formData.newPassword}
              onChange={(e) => handleInputChange('newPassword', e.target.value)}
              sx={{ mb: 2 }}
              helperText="At least 8 characters"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              sx={{ mb: 3 }}
              error={formData.confirmPassword && formData.newPassword !== formData.confirmPassword}
              helperText={formData.confirmPassword && formData.newPassword !== formData.confirmPassword ? 'Passwords do not match' : ''}
            />
            <Button
              onClick={handleResetPassword}
              variant="contained"
              fullWidth
              size="large"
              disabled={!formData.newPassword || !formData.confirmPassword || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Reset Password'}
            </Button>
          </>
        )}

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button variant="text" onClick={onBackToLogin}>
            Back to Sign In
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ForgotPasswordForm;